// どのようにフォーマットされたかを型レベルで保証するためのファイル、スマートコンストラクタ

import { formatNumber } from 'utils/formatter';

const SignSymbol = Symbol('SIGN');
export type Sign =
  | ({ [key in typeof SignSymbol]: never } & 'plus')
  | ({ [key in typeof SignSymbol]: never } & 'zero')
  | ({ [key in typeof SignSymbol]: never } & 'minus');
export const getSign = (num: number): Sign => {
  if (num > 0) return 'plus' as Sign;
  if (num < 0) return 'minus' as Sign;
  return 'zero' as Sign;
};

const CommaFormatted = Symbol('COMMA_FORMATTED');
export type CommaFormattedString = { [key in typeof CommaFormatted]: never } &
  string;
export const commaFormat = <T extends number>(
  num: T,
  option: { numberOfDecimals: number } = { numberOfDecimals: 0 }
): CommaFormattedString & T => {
  return formatNumber(num, {
    numberOfDecimal: option.numberOfDecimals,
  }) as CommaFormattedString & T;
};

const CommaFormattedPercentage = Symbol('COMMA_FORMATTED_PERCENTAGE');
export type CommaFormattedPercentageString = {
  [key in typeof CommaFormattedPercentage]: never;
} &
  string;
export const commaFormatPercentage = (
  rate: number,
  option: { numberOfDecimals: number } = { numberOfDecimals: 0 }
): CommaFormattedPercentageString => {
  return (commaFormat(
    rate * 100,
    option
  ) as unknown) as CommaFormattedPercentageString;
};

const SignAddedCommaFormattedPercentage = Symbol(
  'SIGN_ADDED_COMMA_FORMATTED_PERCENTAGE'
);
export type SignAddedCommaFormattedPercentageString = {
  [key in typeof SignAddedCommaFormattedPercentage]: never;
} &
  string;
export const addSignCommaFormatPercentage = (
  rate: number,
  option: { numberOfDecimals: number } = { numberOfDecimals: 0 }
): SignAddedCommaFormattedPercentageString => {
  return (addSignCommaFormat(
    rate * 100,
    option
  ) as unknown) as SignAddedCommaFormattedPercentageString;
};

const SignAddedCommaFormatted = Symbol('SIGN_ADDED_COMMA_FORMATTED');
export type SignAddedCommaFormattedString = {
  [key in typeof SignAddedCommaFormatted]: never;
} &
  string;
export const addSignCommaFormat = (
  num: number | null,
  option: { numberOfDecimals: number } = { numberOfDecimals: 0 }
): SignAddedCommaFormattedString => {
  if (num === null) return '---' as SignAddedCommaFormattedString;
  if (num === 0)
    return formatNumber(0, {
      numberOfDecimal: option.numberOfDecimals,
    }) as SignAddedCommaFormattedString;
  if (num > 0)
    return formatNumber(num, {
      numberOfDecimal: option.numberOfDecimals,
      withPlusSign: true,
    }) as SignAddedCommaFormattedString;
  if (num < 0)
    return formatNumber(num, {
      numberOfDecimal: option.numberOfDecimals,
      withPlusSign: true,
    }) as SignAddedCommaFormattedString;
  return '' as SignAddedCommaFormattedString;
};
export const extractSign = (str: SignAddedCommaFormattedString): Sign => {
  if (str.charAt(0) === '+') return 'plus' as Sign;
  if (str.charAt(0) === '-') return 'minus' as Sign;
  return 'zero' as Sign;
};

// 10 => 10%増加
// -10 => 10%減少
const PercentAndDeltaVerboseFormatted = Symbol(
  'PERCENT_AND_DELTA_VERBOSE_FORMATTED'
);
export type PercentAndDeltaVerboseFormattedString = {
  [key in typeof PercentAndDeltaVerboseFormatted]: never;
} &
  string;
export const addPercentAndDeltaVerboseFormat = (
  num: number
): PercentAndDeltaVerboseFormattedString => {
  const suffix = ((num: number): string => {
    if (num > 0) return '増加しています。';
    if (num < 0) return '減少しています。';
    return 'と変わりありません。';
  })(num);
  return `${formatNumber(num * 100, {
    withPlusSign: true,
  })}%${suffix}` as PercentAndDeltaVerboseFormattedString;
};

const YieldFormattedPercentage = Symbol('YIELD_FORMATTED_PERCENTAGE');
export type YieldFormattedPercentageString = {
  [key in typeof YieldFormattedPercentage]: never;
} &
  string;
export const yieldFormatPercentage = (
  rateRaw: number,
  option: { numberOfDecimals: number } = { numberOfDecimals: 0 }
): YieldFormattedPercentageString => {
  const numberOfDecimals = option.numberOfDecimals;
  return (Number(
    Math.ceil(rateRaw * 100 * 10 ** numberOfDecimals) / 10 ** numberOfDecimals
  ).toFixed(numberOfDecimals) as unknown) as YieldFormattedPercentageString;
};
